import PropTypes from "prop-types";
import React, { useState } from "react";
import { Alert, Badge, Button, Modal } from "@yoast/ui-library";
import { defineMessages, FormattedMessage, injectIntl, intlShape } from "react-intl";
import { getScopeLabel } from "./scopeLabel";
import * as styles from "./PersonalAccessTokensPageStyles.module.scss";

const messages = defineMessages( {
	title: {
		id: "personalAccessTokens.reveal.title",
		defaultMessage: "Copy your new personal access token",
	},
	ariaLabel: {
		id: "personalAccessTokens.reveal.ariaLabel",
		defaultMessage: "Your newly created personal access token",
	},
	warning: {
		id: "personalAccessTokens.reveal.warning",
		defaultMessage: "This is the only time you will see the full token. Copy it now and store it somewhere safe.",
	},
	copy: {
		id: "personalAccessTokens.reveal.copy",
		defaultMessage: "Copy",
	},
	copied: {
		id: "personalAccessTokens.reveal.copied",
		defaultMessage: "Copied!",
	},
	close: {
		id: "personalAccessTokens.reveal.close",
		defaultMessage: "I have copied the token",
	},
	scopesLabel: {
		id: "personalAccessTokens.reveal.scopesLabel",
		defaultMessage: "Granted permissions",
	},
	copyFailed: {
		id: "personalAccessTokens.reveal.copyFailed",
		defaultMessage: "Could not copy to clipboard. Please select the token manually and copy it.",
	},
} );

/**
 * Modal that shows a newly created personal access token's raw value once,
 * with a copy-to-clipboard action and the granted permissions.
 *
 * @param {Object} props Component props.
 *
 * @returns {JSX.Element} The modal.
 */
function PersonalAccessTokenRevealModal(
	{
		token = null,
		onClose,
		intl,
		availableScopes = [],
	},
) {
	const [ copied, setCopied ]         = useState( false );
	const [ copyFailed, setCopyFailed ] = useState( false );

	const handleCopy = async() => {
		if ( ! token ) {
			return;
		}
		try {
			await navigator.clipboard.writeText( token.token );
			setCopied( true );
			setCopyFailed( false );
		} catch ( e ) {
			setCopied( false );
			setCopyFailed( true );
		}
	};

	const handleClose = () => {
		setCopied( false );
		setCopyFailed( false );
		onClose();
	};

	return (
		<Modal
			isOpen={ Boolean( token ) }
			onClose={ handleClose }
			aria-label={ intl.formatMessage( messages.ariaLabel ) }
		>
			<Modal.Panel>
				<Modal.Title as="h2" size="3">
					<FormattedMessage { ...messages.title } />
				</Modal.Title>
				<div className={ styles.modalBody }>
					<p className={ styles.warning }>
						<FormattedMessage { ...messages.warning } />
					</p>
					{ token && (
						<>
							<div className={ styles.tokenRow }>
								<code className={ styles.tokenValue }>{ token.token }</code>
								<Button variant="secondary" className={ styles.copyButton } onClick={ handleCopy }>
									<FormattedMessage { ...( copied ? messages.copied : messages.copy ) } />
								</Button>
							</div>
							{ copyFailed && (
								<Alert variant="error">
									<FormattedMessage { ...messages.copyFailed } />
								</Alert>
							) }
							{ token.scopes && token.scopes.length > 0 && (
								<p className={ styles.scopesSummary }>
									<strong><FormattedMessage { ...messages.scopesLabel } />:</strong>{ " " }
									<span className={ styles.scopeBadges }>
										{ token.scopes.map( scope => (
											<Badge key={ scope } variant="plain" size="small">
												{ getScopeLabel( scope, availableScopes ) }
											</Badge>
										) ) }
									</span>
								</p>
							) }
						</>
					) }
				</div>
				<div className={ styles.modalActions }>
					<Button variant="primary" onClick={ handleClose }>
						<FormattedMessage { ...messages.close } />
					</Button>
				</div>
			</Modal.Panel>
		</Modal>
	);
}

PersonalAccessTokenRevealModal.propTypes = {
	token: PropTypes.shape( {
		id: PropTypes.string,
		token: PropTypes.string.isRequired,
		scopes: PropTypes.arrayOf( PropTypes.string ),
	} ),
	availableScopes: PropTypes.arrayOf( PropTypes.shape( {
		id: PropTypes.string.isRequired,
		label: PropTypes.string.isRequired,
	} ) ),
	onClose: PropTypes.func.isRequired,
	intl: intlShape.isRequired,
};

export default injectIntl( PersonalAccessTokenRevealModal );
